import axios, { type AxiosRequestConfig } from 'axios';
import { env } from '@config/env';
import { unwrap } from '@shared/api/unwrap';
import { resolvePublicApiKey } from '@shared/lib/publicApiKey';
import { useAuthStore } from '@shared/stores/authStore';

/** Unauthenticated access-invite endpoints (`publicTenantMiddleware` + API key). */

export type PublicMaskedInvite = Record<string, unknown>;

export interface AcceptPublicResult {
  pin?: string;
  qr_payload?: string;
  credential_methods?: string[];
  already_accepted?: boolean;
}

function publicHeaders(hasJsonBody: boolean): Record<string, string> {
  const tenantId = useAuthStore.getState().tenantId;
  const apiKey = resolvePublicApiKey();
  if (!tenantId) {
    throw new Error('Tenant not resolved. Reload the page.');
  }
  if (!apiKey) {
    throw new Error(
      'No API key configured for this domain. For localhost, set developmentApiKey in env or public/runtime-env.js, or add http://localhost:4303 to the domain API key allowed referrers.'
    );
  }
  const headers: Record<string, string> = {
    'X-Tenant-ID': tenantId,
    'X-API-Key': apiKey,
    'x-app-template-id': env.appTemplateId,
  };
  if (hasJsonBody) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

function publicConfig(method: string, body?: FormData | Record<string, unknown>): AxiosRequestConfig {
  const baseURL = useAuthStore.getState().regionalApiUrl || env.apiBaseUrl;
  const isForm = body instanceof FormData;
  return {
    baseURL,
    method,
    data: body,
    headers: publicHeaders(!isForm && body !== undefined),
  };
}

export async function fetchPublicMaskedInvite(token: string): Promise<PublicMaskedInvite> {
  const { data } = await axios.request<{ data: PublicMaskedInvite }>({
    ...publicConfig('GET'),
    url: `/public/access-invites/${encodeURIComponent(token)}`,
  });
  return unwrap<PublicMaskedInvite>(data);
}

export async function acceptPublicInvite(
  token: string,
  opts?: { selfie?: File },
): Promise<AcceptPublicResult> {
  const fd = new FormData();
  if (opts?.selfie) fd.append('selfie', opts.selfie);
  const { data } = await axios.request<{ data: AcceptPublicResult }>({
    ...publicConfig('POST', fd),
    url: `/public/access-invites/${encodeURIComponent(token)}/accept`,
  });
  return unwrap<AcceptPublicResult>(data);
}

export interface AccessOnboardingContext {
  require_onboarding_before_accept?: boolean;
  onboarding_type_id?: string | null;
  flow_instance_id?: string | null;
}

export async function fetchAccessOnboardingContext(
  accessInviteId: string,
  accessToken: string,
): Promise<AccessOnboardingContext> {
  const { data } = await axios.request<{ data: AccessOnboardingContext }>({
    ...publicConfig('GET'),
    url: `/public/access-invites/${encodeURIComponent(accessInviteId)}/${encodeURIComponent(accessToken)}/onboarding-context`,
  });
  return unwrap<AccessOnboardingContext>(data);
}

export async function startAccessOnboarding(
  accessInviteId: string,
  accessToken: string,
): Promise<{ flow_instance_id?: string }> {
  const { data } = await axios.request<{ data: { flow_instance_id?: string } }>({
    ...publicConfig('POST', {}),
    url: `/public/access-invites/${encodeURIComponent(accessInviteId)}/${encodeURIComponent(accessToken)}/onboarding/start`,
  });
  return unwrap(data);
}

export async function completeAccessOnboarding(
  accessInviteId: string,
  accessToken: string,
): Promise<{ pin?: string }> {
  const { data } = await axios.request<{ data: { pin?: string } }>({
    ...publicConfig('POST', {}),
    url: `/public/access-invites/${encodeURIComponent(accessInviteId)}/${encodeURIComponent(accessToken)}/onboarding/complete`,
  });
  return unwrap(data);
}

export interface AccessCredentialReveal {
  reference: string;
  methods: string[];
  pin_available: boolean;
  qr_payload?: string | null;
  issued_credentials?: Array<{ method: string; qr_payload?: string | null }>;
  biometric_hint?: string | null;
}

export async function fetchAccessCredential(
  accessInviteId: string,
  accessToken: string,
): Promise<AccessCredentialReveal> {
  const { data } = await axios.request<{ data: AccessCredentialReveal }>({
    ...publicConfig('GET'),
    url: `/public/access-invites/${encodeURIComponent(accessInviteId)}/${encodeURIComponent(accessToken)}/credential`,
  });
  return unwrap<AccessCredentialReveal>(data);
}
